/** Drawing the Practice screen: the cave grid, the chosen cave's details, the level row. */

import type { CaveDef } from '../../caves/types';
import { BLACK, GREEN, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { Rect } from '../kit/menuModel';
import { drawSpinner } from '../kit/spinner';
import { commas, wrap } from '../kit/text';
import { drawCaveThumb, thumbReady } from '../kit/thumbs';
import { drawUncover } from '../kit/uncover';
import { FAINT, HEADING, INK, MUTED, SELECTED } from '../kit/theme';
import { GRID_COLS, GRID_ROWS } from './practiceModel';

export interface PracticeEntry {
  readonly def: CaveDef;
  /** Cache key for the player's own caves (their letters are not unique). */
  readonly key?: string;
  /** The letter shown on the picture ('' for the player's own). */
  readonly badge: string;
}

export const GRID_X = 10;
export const GRID_Y = 37;
export const CELL_W = 60;
export const CELL_H = 26;
export const LEVEL_Y = 178;
export const PLAY_RECT: Rect = { x: 236, y: 174, w: 76, h: 15 };
export const KIT_RECT: Rect = { x: 80, y: 104, w: 160, h: 16 };

/** The grid cell of visible slot `slot` (0 = top-left of the scrolled view). */
export function cellRect(slot: number): Rect {
  const col = slot % GRID_COLS;
  const row = Math.floor(slot / GRID_COLS);
  return { x: GRID_X + col * CELL_W, y: GRID_Y + row * CELL_H, w: CELL_W, h: CELL_H };
}

export function levelChip(i: number): Rect {
  return { x: 64 + i * 18, y: LEVEL_Y - 3, w: 16, h: 13 };
}

/**
 * The visible part of the grid. `budget` pictures may be made this frame; the rest show cover tiles
 * until their turn. Returns how many were made.
 */
export function drawGrid(
  screen: Screen, entries: readonly PracticeEntry[], scroll: number, sel: number, focused: boolean, hot: number,
  level: number, best: (e: PracticeEntry) => number, ms: number, budget: number,
): number {
  let made = 0;
  for (let slot = 0; slot < GRID_COLS * GRID_ROWS; slot++) {
    const i = scroll * GRID_COLS + slot;
    const e = entries[i];
    if (!e) break;
    const c = cellRect(slot);
    const box = { x: c.x + 8, y: c.y + 1, w: 44, h: 24 };
    const ready = thumbReady(e.def, level, box, e.key);
    if (ready || made < budget) {
      if (!ready) made++;
      drawCaveThumb(screen, e.def, level, box, e.key);
    } else drawUncover(screen, box, 0, i, e.def.colors, 8);
    if (e.badge) {
      screen.fill(box.x - 1, box.y - 1, 10, 10, BLACK);
      screen.text(e.badge, box.x, box.y, e.def.intermission ? YELLOW : INK);
    }
    if (best(e) > 0) {
      screen.fill(box.x + box.w - 9, box.y + box.h - 9, 10, 10, BLACK);
      screen.text('✓', box.x + box.w - 8, box.y + box.h - 8, GREEN);
    }
    const frame = i === sel ? (focused ? SELECTED : MUTED) : i === hot ? INK : -1;
    if (frame >= 0) screen.box(box.x - 2, box.y - 2, box.w + 4, box.h + 4, frame);
    if (i === sel && focused) drawSpinner(screen, c.x - 3, c.y + 9, ms);
  }
  return made;
}

/** Name, best score and hint of the selected cave. */
export function drawDetails(screen: Screen, e: PracticeEntry, best: number, level: number): void {
  const name = e.badge ? `${e.badge}  ${e.def.name}` : e.def.name;
  screen.text(name, GRID_X, 146, SELECTED);
  const bestText = best > 0 ? `BEST ${commas(best)}` : `NOT WON AT LEVEL ${level}`;
  screen.text(bestText, 310, 146, best > 0 ? INK : FAINT, 'right');
  wrap(e.def.hint || 'A CAVE OF YOUR OWN MAKING.', 37).slice(0, 2).forEach((line, i) => {
    screen.text(line, GRID_X, 157 + i * 9, MUTED);
  });
}

export function drawLevelRow(screen: Screen, level: number, focused: boolean, ms: number): void {
  if (focused) drawSpinner(screen, 0, LEVEL_Y, ms);
  screen.text('LEVEL', GRID_X, LEVEL_Y, focused ? SELECTED : HEADING);
  for (let i = 0; i < 5; i++) {
    const r = levelChip(i);
    const on = i === level - 1;
    if (on) screen.box(r.x, r.y, r.w, r.h, focused ? SELECTED : MUTED);
    screen.text(String(i + 1), r.x + r.w / 2, LEVEL_Y, on ? (focused ? SELECTED : INK) : MUTED, 'center');
  }
}

/** MY CAVES with nothing in it: how to make one. */
export function drawEmptyLibrary(screen: Screen): void {
  screen.text('NO CAVES OF YOUR OWN YET.', 160, 56, INK, 'center');
  screen.text('BUILD ONE IN THE CONSTRUCTION KIT,', 160, 72, MUTED, 'center');
  screen.text('SAVE IT AND IT WILL APPEAR HERE.', 160, 82, MUTED, 'center');
}
